<script setup lang="ts">
import type { RouteLocationNormalized, RouteMeta } from '#vue-router';

const { $appConfig } = useNuxtApp();
const config = $appConfig.app;

const route = useRoute();
const routeBaseName = useRouteBaseName();
const head = useLocaleHead();
const { t, locale } = useI18n();
const { initBeacon, switchBeacon } = useHelpscoutBeacon();
const partnerId = usePartnerId();
const gtmStore = useGtmStore();

const gtmContainerID = config?.layouts?.default?.gtmContainerID;

// Consent Mode defaults must precede the gtm.js start event, and nothing else in the app declares them.
// https://developers.google.com/tag-platform/security/guides/consent
if (gtmContainerID) {
  const { proxy } = useScriptGoogleTagManager({
    id: gtmContainerID,
    // The script is inserted after hydration, at idle; a preload would fetch it at parse time and
    // Chrome warns when the two are more than three seconds apart. Warm the connection only.
    scriptOptions: { warmupStrategy: 'preconnect' },
    onBeforeGtmStart: (gtag) => {
      gtag('consent', 'default', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied',
        functionality_storage: 'denied',
        personalization_storage: 'denied',
        security_storage: 'granted',
        wait_for_update: 500,
      });
    },
  });
  // Registered once, here: a second useScript call would attach a handler to the calling component's scope.
  gtmStore.connect(proxy.dataLayer);
}

// Declared BEFORE the computeds despite the canonical methods-after-reactive order: useSeoMeta
// below evaluates the computeds synchronously during setup, and a later declaration is a TDZ
// ReferenceError on every page load (caught in execution by the e2e harness).
// config.title is a partner-provided literal, not an i18n key: translating it would look up a
// missing key (latent — nothing populates it today). Only meta.title and the fallback are keys.
const pageTitle = (meta: RouteMeta) => (meta.title ? t(meta.title) : (config?.title ?? t('app.title')));
const pageDescription = (meta: RouteMeta) => t(meta.description ?? 'app.seo.description');

// computed: the title must follow SPA navigations — a plain const freezes the entry page's
// title in the tab, the history and the route announcer.
const title = computed(() => pageTitle(route.meta));
const description = computed(() => pageDescription(route.meta));

// SEO — no manual ogTitle: nuxt-seo-utils derives it from the title, and og:site_name already
// carries the brand (site.name); concatenating it here doubled the mark with another separator.
useSeoMeta({
  title,
  description,
  ogDescription: description,
});

watch(locale, switchBeacon);

// The document cache policy (route cache, CDN headers) is decided before any render, redirects included:
// server/plugins/08.document-cache.ts.

// page_view leaves from the router, never from a GTM History Change trigger: the two replaceState
// rewrites must stay silent — docs/tag-plan.md § Three traps.
let trackedPath: string | undefined;
// The store cannot read the route, the locale or the cookie itself: a composable in a getter runs with no scope.
const syncPageContext = (to: RouteLocationNormalized) => {
  gtmStore.setPageContext({ routeBaseName: routeBaseName(to), language: locale.value, partnerId: partnerId.value });
};
const trackPageView = async (path: string) => {
  if (path === trackedPath) {
    return;
  }
  trackedPath = path;
  // unhead applies the title after the route resolves, so it is only correct a tick later.
  await nextTick();
  gtmStore.trackPageView(globalThis.location.href, globalThis.document.title);
};

syncPageContext(route);
const stopPageTracking = useRouter().afterEach((to) => {
  syncPageContext(to);
  void trackPageView(to.path);
  if (!globalThis.Beacon?.('info')) return;
  // Same fallback chain as the tab title: only 5 pages declare meta.title, and t(undefined)
  // throws — killing this very event on every funnel navigation.
  globalThis.Beacon('event', { type: 'page-viewed', url: to.fullPath, title: pageTitle(to.meta) });
});

onMounted(() => {
  void initBeacon();
  void trackPageView(route.path);
});
onUnmounted(stopPageTracking);
</script>

<template>
  <Html
    :lang="head.htmlAttrs?.lang"
    :dir="head.htmlAttrs?.dir"
    class="motion-safe:scroll-smooth"
  >
    <Head>
      <template
        v-for="link in head.link"
        :key="link.id"
      >
        <Link
          :id="link.id"
          :rel="link.rel"
          :href="link.href"
          :hreflang="link.hreflang"
          :nonce="link.nonce"
        />
      </template>
      <template
        v-for="meta in head.meta"
        :key="meta.id"
      >
        <Meta
          :id="meta.id"
          :property="meta.property"
          :content="meta.content"
        />
      </template>
    </Head>
    <NuxtLoadingIndicator />
    <NuxtRouteAnnouncer />
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
    <UiToaster />
  </Html>
</template>
